import { env } from '@/config/env';

/**
 * The `Auth0Provider` configuration (everything except the router-bound
 * `onRedirectCallback`), extracted from `Providers.tsx` so the
 * security-critical token-storage choice is covered by a unit test.
 *
 * **Token storage — `cacheLocation` (2026-07 pentest hardening):**
 *
 * On a DEPLOYED origin (app.syncanix.com / app.dev.syncanix.com) tokens are
 * cached in `'memory'`, NOT `localStorage`. Persisting the access/id tokens in
 * localStorage made them readable by any JavaScript on the origin, so a single
 * DOM-XSS (or a compromised CSP-allowed script) could exfiltrate them and
 * impersonate the tenant-admin until expiry. In memory the tokens never touch a
 * persistent, JS-readable store. Cross-reload continuity rides the silent-auth
 * iframe (`/authorize?prompt=none`), which is reliable there because the app and
 * Auth0 (auth.syncanix.com) are the SAME SITE (both under syncanix.com), so the
 * Auth0 session cookie is a first-party cookie in the iframe.
 *
 * On `localhost` (local dev + the Playwright auth E2E) we deliberately fall back
 * to `'localstorage'`. Two reasons: (1) there is no XSS-exfil threat model on a
 * developer's own machine; (2) locally the app (localhost) and Auth0
 * (auth.syncanix.com) are CROSS-SITE, so the silent-auth iframe cookie is blocked
 * and memory-mode reload cannot restore the session — which would break the
 * storageState-reuse auth harness (`e2e/auth.setup.ts`). Keying on the real
 * origin's hostname is safe: a deployed origin can never report `localhost`, so
 * production always gets memory.
 *
 * A regression that makes a DEPLOYED origin use localStorage fails
 * `auth0-config.test.ts`.
 */

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/** Whether the app is served from a local (non-deployed) origin. */
export function isLocalOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  return LOCAL_HOSTNAMES.has(window.location.hostname);
}

export function resolveCacheLocation(): 'memory' | 'localstorage' {
  return isLocalOrigin() ? 'localstorage' : 'memory';
}

export function buildAuth0ProviderConfig() {
  return {
    domain: env.VITE_AUTH0_DOMAIN,
    clientId: env.VITE_AUTH0_CLIENT_ID,
    authorizationParams: {
      redirect_uri: env.VITE_AUTH0_REDIRECT_URI,
      audience: env.VITE_AUTH0_AUDIENCE,
    },
    cacheLocation: resolveCacheLocation(),
  };
}
